import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, CircularProgress } from '@mui/material';
import { Add, Edit, Delete, Block, CheckCircle, PersonAdd, Article } from '@mui/icons-material';
import { useAppSelector } from '../../hooks/useAppHooks';
import apiClient from '../../utils/apiClient';
import { User } from '../../types';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<{ email: string; password: string; role: 'super_admin' | 'admin' | 'customer' }>({ email: '', password: '', role: 'customer' });
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTarget, setLogsTarget] = useState<string>('');

  const fetchUsers = async () => {
    setIsLoading(true);
    try { const r = await apiClient.get<User[]>('/admin/users'); setUsers(r.data); setError(null); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed to fetch users'); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleOpenDialog = (user?: User) => {
    if (user) { setEditUser(user); setFormData({ email: user.email, password: '', role: user.role }); }
    else { setEditUser(null); setFormData({ email: '', password: '', role: 'customer' }); }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editUser) await apiClient.patch(`/admin/users/${editUser.id}`, { email: formData.email, role: formData.role });
      else await apiClient.post('/admin/users', formData);
      fetchUsers(); setDialogOpen(false);
    } catch (e: any) { setError(e.response?.data?.detail || 'Operation failed'); }
  };

  const handleToggleActive = async (user: User) => {
    try { await apiClient.patch(`/admin/users/${user.id}`, { is_active: !user.is_active }); fetchUsers(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Delete this user?')) return;
    try { await apiClient.delete(`/admin/users/${userId}`); fetchUsers(); }
    catch (e: any) { setError(e.response?.data?.detail || 'Failed'); }
  };

  const openUserLogs = async (user: User) => {
    setLogsTarget(user.email);
    setLogsDialogOpen(true);
    setLogsLoading(true);
    try {
      const response = await apiClient.get('/admin/audit-logs', {
        params: { target_type: 'user', target_id: user.id, limit: 50 }
      });
      setAuditLogs(response.data.logs || []);
    } catch {
      setAuditLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>User Management</Typography>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => handleOpenDialog()}>Add User</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card><CardContent sx={{ p: 0 }}>
        {isLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
          <TableContainer><Table>
            <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell><TableCell>Created</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={500}>{user.email}</Typography></TableCell>
                  <TableCell><Chip label={user.role.replace('_', ' ')} size="small" color={user.role === 'super_admin' ? 'secondary' : 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={user.is_active ? 'Active' : 'Inactive'} size="small" sx={{ backgroundColor: user.is_active ? 'rgba(105, 240, 174, 0.2)' : 'rgba(255, 82, 82, 0.2)', color: user.is_active ? '#69f0ae' : '#ff5252' }} /></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{new Date(user.created_at).toLocaleDateString()}</Typography></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(user)} disabled={user.id === currentUser?.id}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => openUserLogs(user)} title="View Logs"><Article fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleToggleActive(user)} disabled={user.id === currentUser?.id}>{user.is_active ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}</IconButton>
                    {currentUser?.role === 'super_admin' && <IconButton size="small" color="error" onClick={() => handleDelete(user.id)} disabled={user.id === currentUser?.id}><Delete fontSize="small" /></IconButton>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
        )}
      </CardContent></Card>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent><Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} fullWidth required />
          {!editUser && <TextField label="Password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} fullWidth required />}
          <TextField label="Role" select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value as any })} fullWidth>
            <MenuItem value="customer">Customer</MenuItem><MenuItem value="admin">Admin</MenuItem>{currentUser?.role === 'super_admin' && <MenuItem value="super_admin">Super Admin</MenuItem>}
          </TextField>
        </Box></DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSubmit}>{editUser ? 'Update' : 'Create'}</Button></DialogActions>
      </Dialog>

      {/* Audit Logs Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Logs — {logsTarget}</DialogTitle>
        <DialogContent>
          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : auditLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No logs found</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                          {log.details ? JSON.stringify(log.details) : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
